import { Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { firstValueFrom } from 'rxjs'
import { ToastService } from '@common/services'
import { ElectronicProcessBook, ElectronicProcessBookUnit } from '@models/planning/electronic-process-book.model'
import { ElectronicProcessBookService } from '@services/electronic-process-book.service'
import { PdfJsLoaderService } from '@services/pdfjs-loader.service'
import { PlatformService } from '@services/platform.service'
import { NativeDocumentService } from '@services/native-document.service'
import { downloadByLink } from '@utils/download-link.util'

/** Leitura por faixas de 256 KiB, sem baixar o resto em segundo plano: só as páginas exibidas (spike, D12 e D14). */
export const BOOK_PDF_OPTIONS = { disableAutoFetch: true, disableStream: true, rangeChunkSize: 262144 }

/** Antecedência para pedir um link novo antes de o atual vencer (CA-18). */
const RENEW_BEFORE_MS = 60_000
const MIN_RENEW_DELAY_MS = 10_000
const ZOOM_STEP = 1.2

/** `ScrollMode.PAGE` do pdf.js, que ele impõe acima de 10.000 páginas (`PagesCountLimit.FORCE_SCROLL_MODE_PAGE`). */
const SCROLL_MODE_PAGE = 3
/** Quanto rolar além da borda da folha, num gesto, para virar a página. */
const WHEEL_TURN_DISTANCE = 120
/** Intervalo entre eventos da roda que separa um gesto do outro: o gesto que virou a página não vira a seguinte. */
const WHEEL_GESTURE_GAP_MS = 200

/** Teclas que viram a página quando o livro é exibido uma folha por vez. */
const PAGE_KEYS: Record<string, 'next' | 'previous' | 'first' | 'last'> = {
	PageDown: 'next',
	ArrowRight: 'next',
	PageUp: 'previous',
	ArrowLeft: 'previous',
	Home: 'first',
	End: 'last'
}

const isTypingTarget = (target: EventTarget | null): boolean =>
	target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))

/** A folha pedida, dentro do livro. */
export const clampFolha = (folha: number, pages: number): number => Math.min(Math.max(Math.trunc(folha) || 1, 1), Math.max(pages, 1))

/** Daqui a quanto tempo pedir um link novo, ou nulo sem prazo. */
export const renewDelay = (expiresAt: string | null | undefined, now = Date.now()): number | null => {
	if (!expiresAt) return null

	return Math.max(new Date(expiresAt).getTime() - now - RENEW_BEFORE_MS, MIN_RENEW_DELAY_MS)
}

/**
 * Visualizador do livro eletrônico (spec 001, RF 15 a 19): o PDF único aberto pelo pdf.js, que lê só as páginas
 * visíveis por requisições de faixa, com o sumário dos documentos e anexos ao lado e "ir para a folha". A página N do
 * arquivo é a folha N. O link de visualização é renovado antes de vencer, sem gerar o livro de novo (CA-18).
 *
 * Acima de 10.000 páginas o pdf.js exibe uma folha por vez e não deixa voltar à rolagem contínua. Nesse modo, a
 * página vira pelas setas do cabeçalho, pelo teclado e pela roda do mouse na borda da folha.
 */
@Component({
	selector: 'app-electronic-process-book-viewer',
	templateUrl: './electronic-process-book-viewer.component.html',
	styleUrl: './electronic-process-book-viewer.component.scss',
	standalone: false
})
export class ElectronicProcessBookViewerComponent implements OnInit, OnDestroy {
	@Input() processId: string
	@Input() book: ElectronicProcessBook
	@Input() processNumber: string | null = null

	@ViewChild('viewerContainer', { static: true }) viewerContainer: ElementRef<HTMLDivElement>
	@ViewChild('viewerElement', { static: true }) viewerElement: ElementRef<HTMLDivElement>

	loading = true
	error: string | null = null
	pageNumber = 1
	pagesCount = 0
	folhaInput: number | null = null
	downloading = false
	summaryOpen = true
	/** O pdf.js exibe uma folha por vez (livro com mais de 10.000 folhas). */
	singlePage = false

	private pdfjs?: { lib: any; viewer: any }
	private pdfViewer?: any
	private linkService?: any
	private loadingTask?: any
	private renewTimer?: ReturnType<typeof setTimeout>
	private destroyed = false
	/** Folha a abrir quando as páginas do documento ficarem prontas. */
	private pendingPage = 1
	private readonly listeners = new AbortController()
	private wheelOverscroll = 0
	private lastWheelAt = Number.NEGATIVE_INFINITY
	private wheelLocked = false

	constructor(
		public activeModal: NgbActiveModal,
		private zone: NgZone,
		private toast: ToastService,
		private bookService: ElectronicProcessBookService,
		private pdfJsLoader: PdfJsLoaderService,
		private platform: PlatformService,
		private nativeDocument: NativeDocumentService
	) {}

	ngOnInit(): void {
		void this.open()
	}

	ngOnDestroy(): void {
		this.destroyed = true
		this.listeners.abort()
		clearTimeout(this.renewTimer)
		void this.loadingTask?.destroy()
		this.pdfViewer?.setDocument(null)
	}

	get summary(): ElectronicProcessBookUnit[] {
		return this.book?.summary ?? []
	}

	/** O arquivo do sumário em que está a folha aberta. */
	isCurrent(unit: ElectronicProcessBookUnit): boolean {
		return this.pageNumber >= (unit.firstFolha ?? 0) && this.pageNumber <= (unit.lastFolha ?? unit.firstFolha ?? 0)
	}

	goToFolha(folha: number | null | undefined): void {
		if (!this.pdfViewer || !this.pagesCount || folha == null) return

		// `currentPageNumber`, não `#page=N`: o visualizador pede só as faixas da página nova (plano, pdf.js).
		this.pdfViewer.currentPageNumber = clampFolha(folha, this.pagesCount)
		this.folhaInput = null
	}

	nextPage(): void {
		this.pdfViewer?.nextPage()
	}

	/** Folha anterior; com [fromBottom], mostra o fim dela, como quem volta rolando. */
	previousPage(fromBottom = false): void {
		if (!this.pdfViewer?.previousPage() || !fromBottom) return

		const container = this.viewerContainer.nativeElement
		requestAnimationFrame(() => (container.scrollTop = container.scrollHeight))
	}

	@HostListener('document:keydown', ['$event'])
	onKeydown(event: KeyboardEvent): void {
		const action = PAGE_KEYS[event.key]
		if (!this.singlePage || !action || event.altKey || event.ctrlKey || event.metaKey || isTypingTarget(event.target)) return

		event.preventDefault()

		if (action === 'next') this.nextPage()
		else if (action === 'previous') this.previousPage()
		else this.goToFolha(action === 'first' ? 1 : this.pagesCount)
	}

	zoomIn(): void {
		if (this.pdfViewer) this.pdfViewer.currentScale = Math.min(this.pdfViewer.currentScale * ZOOM_STEP, 4)
	}

	zoomOut(): void {
		if (this.pdfViewer) this.pdfViewer.currentScale = Math.max(this.pdfViewer.currentScale / ZOOM_STEP, 0.25)
	}

	fitWidth(): void {
		if (this.pdfViewer) this.pdfViewer.currentScaleValue = 'page-width'
	}

	async download(): Promise<void> {
		if (this.downloading || !this.book?.id) return

		this.downloading = true

		try {
			const link = await firstValueFrom(this.bookService.downloadUrl(this.processId, this.book.id))

			if (this.platform.mobile) {
				await this.nativeDocument.openPdf(link.url, link.filename ?? 'livro-eletronico.pdf')
			} else {
				this.saveLink(link.url, link.filename)
			}
		} finally {
			this.downloading = false
		}
	}

	/** Entrega o link ao gerenciador de downloads do navegador. Separado para o teste não navegar a página. */
	protected saveLink(url: string, filename?: string | null): void {
		downloadByLink(url, filename)
	}

	close(): void {
		this.activeModal.close()
	}

	/** Abre (ou reabre, com link novo) o livro na folha [page]. */
	async open(page = 1): Promise<void> {
		this.loading = true
		this.error = null

		try {
			const [pdfjs, link] = await Promise.all([this.pdfJsLoader.load(), firstValueFrom(this.bookService.viewUrl(this.processId, this.book.id))])
			if (this.destroyed) return

			this.pdfjs = pdfjs
			this.ensureViewer(page)

			await this.loadingTask?.destroy()
			this.loadingTask = pdfjs.lib.getDocument({ url: link.url, ...BOOK_PDF_OPTIONS })
			const document = await this.loadingTask.promise
			if (this.destroyed) return

			this.pagesCount = document.numPages
			this.pendingPage = page
			this.zone.runOutsideAngular(() => {
				this.pdfViewer.setDocument(document)
				this.linkService.setDocument(document)
			})
			this.singlePage = this.pdfViewer.scrollMode === (pdfjs.viewer.ScrollMode?.PAGE ?? SCROLL_MODE_PAGE)

			this.scheduleRenewal(link.expiresAt)
		} catch (error) {
			this.error = this.describe(error)
		} finally {
			this.loading = false
		}
	}

	private ensureViewer(page: number): void {
		if (this.pdfViewer) return

		const { viewer } = this.pdfjs
		const eventBus = new viewer.EventBus()

		this.linkService = new viewer.PDFLinkService({ eventBus })
		this.pdfViewer = this.zone.runOutsideAngular(
			() =>
				new viewer.PDFViewer({
					container: this.viewerContainer.nativeElement,
					viewer: this.viewerElement.nativeElement,
					eventBus,
					linkService: this.linkService,
					// Sem camada de texto e de anotações: o livro é para ler e navegar, e cada camada a mais é trabalho por página.
					textLayerMode: 0,
					annotationMode: this.pdfjs.lib.AnnotationMode?.DISABLE ?? 0,
					removePageBorders: false
				})
		)
		this.linkService.setViewer(this.pdfViewer)
		this.pendingPage = page

		eventBus.on('pagesinit', () => {
			this.pdfViewer.currentScaleValue = 'page-width'
			if (this.pendingPage > 1) this.pdfViewer.currentPageNumber = clampFolha(this.pendingPage, this.pagesCount)
		})
		eventBus.on('pagechanging', (event: { pageNumber: number }) => this.zone.run(() => (this.pageNumber = event.pageNumber)))

		this.zone.runOutsideAngular(() =>
			this.viewerContainer.nativeElement.addEventListener('wheel', event => this.onWheel(event), { passive: true, signal: this.listeners.signal })
		)
	}

	/**
	 * Uma folha por vez: rolar além do fim da folha vira para a próxima, e além do começo, para a anterior. Uma virada
	 * por gesto, para a inércia do touchpad não passar várias folhas de uma vez.
	 */
	private onWheel(event: WheelEvent): void {
		const gap = event.timeStamp - this.lastWheelAt
		this.lastWheelAt = event.timeStamp

		if (!this.singlePage || event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
		if (this.wheelLocked && gap < WHEEL_GESTURE_GAP_MS) return

		this.wheelLocked = false

		const container = this.viewerContainer.nativeElement
		const down = event.deltaY > 0
		const atEdge = down ? container.scrollTop + container.clientHeight >= container.scrollHeight - 1 : container.scrollTop <= 0
		const hasPage = down ? this.pageNumber < this.pagesCount : this.pageNumber > 1

		if (!atEdge || !hasPage) {
			this.wheelOverscroll = 0
			return
		}

		const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? container.clientHeight : 1
		this.wheelOverscroll += Math.abs(event.deltaY) * unit
		if (this.wheelOverscroll < WHEEL_TURN_DISTANCE) return

		this.wheelOverscroll = 0
		this.wheelLocked = true

		if (down) this.nextPage()
		else this.previousPage(true)
	}

	/** Pede um link novo um minuto antes do atual vencer e reabre na mesma folha. */
	private scheduleRenewal(expiresAt: string | null | undefined): void {
		clearTimeout(this.renewTimer)

		const delay = renewDelay(expiresAt)
		if (delay == null) return

		this.renewTimer = setTimeout(() => {
			if (!this.destroyed) void this.open(this.pageNumber)
		}, delay)
	}

	private describe(error: unknown): string {
		if (error instanceof HttpErrorResponse) {
			if (error.status === 410) return 'Os arquivos deste livro expiraram. Gere o livro novamente.'
			if (error.status === 409) return 'O livro ainda não está pronto.'
		}

		return 'Não foi possível abrir o livro. Verifique a conexão e tente de novo.'
	}
}
